<template>
  <div class="modern-form">
    <v-container>
      <div v-if="loading" class="loading-container">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
          class="loading-spinner" />
      </div>
      <v-row v-else class="mt-3">
        <v-col cols="12" sm="12" md="4">
          <div class="form-sidebar">
            <div class="sidebar-header">
              <h3 class="sidebar-title">Forms</h3>
              <v-btn class="btn-primary btn-sm" @click="createNewForm">
                <v-icon left small>mdi-plus</v-icon>
                New
              </v-btn>
            </div>
            <div class="sidebar-content">
              <div class="form-list">
                <div v-if="forms.length === 0" class="no-results">
                  <v-icon class="no-results-icon">mdi-file-document-outline</v-icon>
                  <p class="no-results-text">No forms configured</p>
                </div>
                <div v-else class="forms-list">
                  <div
                    v-for="(form, i) in forms"
                    :key="i"
                    :id="form.id"
                    @click="selectForm(form, i)"
                    class="form-item"
                    :class="{ 'selected': selectedFormIndex === i }">
                    <v-icon class="form-icon">mdi-file-document</v-icon>
                    <span class="form-name">{{ form.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-sidebar mt-4">
            <div class="sidebar-header">
              <h3 class="sidebar-title">Disease Question Groupings</h3>
              <v-btn class="btn-primary btn-sm" @click="selectCategory()">
                <v-icon left small>mdi-plus</v-icon>
                New
              </v-btn>
            </div>
            <div class="sidebar-content">
              <div class="form-list">
                <div v-if="diseaseQuestionGroupings.length === 0" class="no-results">
                  <v-icon class="no-results-icon">mdi-folder-outline</v-icon>
                  <p class="no-results-text">No groupings configured</p>
                </div>
                <div v-else class="forms-list">
                  <div
                    v-for="(category, i) in diseaseQuestionGroupings"
                    :key="i"
                    @click="selectCategory(category, i)"
                    class="form-item"
                    :class="{ 'selected': selectedCategoryIndex === i }">
                    <v-icon class="form-icon">mdi-folder</v-icon>
                    <span class="form-name">{{ category.category }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <v-col cols="12" sm="12" md="8">
          <div v-if="!selectedForm && !selectedCategory" class="no-selection">
            <v-icon class="no-selection-icon">mdi-file-document-outline</v-icon>
            <p class="no-selection-text">Select a form or grouping to edit</p>
          </div>

          <div v-if="selectedForm" class="info-section">
            <div class="section-header-with-actions">
              <h2 class="section-title">Form Configuration</h2>
            </div>

            <v-form ref="form" @submit.prevent="submit" lazy-validation>
              <v-text-field
                label="Form Title"
                hide-details="auto"
                class="line-input custom-filled mb-4"
                placeholder="New Form"
                v-model="selectedForm.name"
                validate-on-blur
                dense
                :rules="[rules.required, rules.max50]" />

              <v-checkbox label="Assign to patients at signup?" v-model="selectedForm.assignAtSignUp" hide-details class="mb-4" />

              <div class="form-preview" @click="goToTemplateEditor">
                <div v-if="selectedForm.body" class="preview-container">
                  <iframe class="preview-iframe" :srcdoc="selectedForm.body" />
                  <div class="preview-overlay"></div>
                </div>
                <div v-else class="preview-empty">
                  <v-icon class="preview-empty-icon">mdi-file-edit-outline</v-icon>
                  <p class="preview-empty-text">Click to open form editor</p>
                </div>
              </div>

              <div class="form-actions">
                <v-btn class="btn-primary" @click="saveForm()">Save</v-btn>
                <v-btn class="btn-secondary" @click="clearFormSelection()">Cancel</v-btn>
                <v-btn class="btn-warning" :disabled="!selectedForm.id" @click="showDeleteDialog = true">Delete</v-btn>
              </div>
            </v-form>
          </div>

          <div v-if="selectedCategory" class="info-section">
            <div class="section-header-with-actions">
              <h2 class="section-title">Disease Grouping Configuration</h2>
            </div>

            <v-text-field
              label="Grouping Title"
              hide-details="auto"
              class="line-input custom-filled mb-4"
              placeholder="New Grouping"
              v-model="selectedCategory.category"
              validate-on-blur
              dense
              :rules="[rules.required, rules.max50]" />

            <h4 class="subsection-title">Included Conditions</h4>

            <v-autocomplete
              class="line-input custom-filled mb-4"
              v-model="selectedCategory.openDentalDiseaseIds"
              :items="allOpenDentalDiseases"
              item-text="DiseaseName"
              item-value="DiseaseDefNum"
              label="Select Disease Mappings"
              multiple
              dense
              hide-details="auto" />

            <div class="form-actions">
              <v-btn class="btn-primary" @click="saveCategory()">Save</v-btn>
              <v-btn class="btn-secondary" @click="clearCategorySelection()">Cancel</v-btn>
              <v-btn class="btn-warning" :disabled="!selectedCategory.id" @click="showDeleteCategoryDialog = true">Delete</v-btn>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-dialog v-model="showDeleteDialog" max-width="500px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Delete Form</v-card-title>
          <v-card-text class="modal-content">
            <p class="modal-question">Are you sure you want to delete {{ selectedForm?.name }}?</p>
            <p class="modal-description">This action cannot be undone.</p>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showDeleteDialog = false">Cancel</v-btn>
            <v-btn class="btn-warning" @click="deleteForm()">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showDeleteCategoryDialog" max-width="500px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Delete Grouping</v-card-title>
          <v-card-text class="modal-content">
            <p class="modal-question">Are you sure you want to delete {{ selectedCategory?.category }}?</p>
            <p class="modal-description">This action cannot be undone.</p>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showDeleteCategoryDialog = false">Cancel</v-btn>
            <v-btn class="btn-warning" @click="deleteCategory()">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>
<script>
import DataService from '../../../_services/DataService';
import ValidateService from '../../../_services/ValidateService';

export default {
  name: 'FormManagement',
  data() {
    return {
      forms: [],
      diseaseQuestionGroupings: [],
      selectedForm: null,
      selectedCategory: null,
      selectedFormIndex: null,
      selectedCategoryIndex: null,
      allOpenDentalDiseases: [],
      loading: true,
      showDeleteDialog: false,
      showDeleteCategoryDialog: false,
      rules: ValidateService.validators,
    };
  },
  methods: {
    async closeAndReload() {
      await this.getClientInfo();
      this.dialogSuccess = false;
    },
    createNewForm() {
      this.selectedCategory = null;
      this.selectedCategoryIndex = null;
      this.selectedFormIndex = null;

      this.selectedForm = {
        name: 'New Form',
        assignAtSignUp: false,
        json: {},
        body: '',
      };

      if (this.$route.query.id) {
        this.$router.replace({ query: { tab: 'form management', id: '' } });
      }
    },
    clearFormSelection() {
      this.selectedForm = null;
      this.selectedFormIndex = null;
      if (this.$route.query.id) {
        this.$router.replace({ query: { tab: 'form management' } });
      }
    },
    clearCategorySelection() {
      this.selectedCategory = null;
      this.selectedCategoryIndex = null;
    },
    async getDiseaseDefinitions() {
      const response = await DataService.getDiseaseDefinitions();
      if (response.status === 200) {
        this.allOpenDentalDiseases = response.data;
      }
    },
    async getClientDiseaseCategories() {
      const response = await DataService.getClientDiseaseCategories();
      if (response.status === 200) {
        this.diseaseQuestionGroupings = response.data;
      }
    },
    async getClientForms() {
      const response = await DataService.getClientForms();
      if (response.status === 200) {
        this.forms = response.data;
      }
    },
    async saveForm(disableAlert) {
      if (this.selectedForm.id) {
        const response = await DataService.updateForm(this.selectedForm.id, { name: this.selectedForm.name, assignAtSignUp: this.selectedForm.assignAtSignUp });
        if (response.status === 200) {
          if (!disableAlert) {
            this.$root.$emit('raise_alert', { color: 'success', text: 'Form updated successfully' });
          }
          return true;
        }

        this.$root.$emit('raise_alert', { color: 'error', text: response.data || 'Failed to update form' });
      } else {
        const response = await DataService.createForm(this.selectedForm);
        
        if (response.status === 201) {
          this.$router.push({ query: { tab: 'form management', id: response.data.id }});
          if (!disableAlert) {
            this.$root.$emit('raise_alert', { color: 'success', text: 'Form created successfully' });
          }
          return true;
        }

        this.$root.$emit('raise_alert', { color: 'error', text: response.data || 'Failed to create form' });
      }

      return false;
    },
    async saveCategory() {
      if (this.selectedCategory.id) {
        const response = await DataService.updateClientDiseaseCategory(this.selectedCategory.id, this.selectedCategory);
        if (response.status === 200) {
          this.$root.$emit('raise_alert', { color: 'success', text: 'Disease grouping updated successfully' });
        } else {
          this.$root.$emit('raise_alert', { color: 'error', text: response.data || 'Failed to update disease grouping' });
        }
      } else {
        const response = await DataService.createClientDiseaseCategory(this.selectedCategory);
        
        if (response.status === 201) {
          this.diseaseQuestionGroupings.push(response.data);
          this.selectedCategory = this.diseaseQuestionGroupings[this.diseaseQuestionGroupings.length - 1];
          this.selectedCategoryIndex = this.diseaseQuestionGroupings.length - 1;
          this.$root.$emit('raise_alert', { color: 'success', text: 'Disease grouping created successfully' });
        } else {
          this.$root.$emit('raise_alert', { color: 'error', text: response.data || 'Failed to create disease grouping' });
        }
      }
    },
    async deleteForm() {
      const id = this.selectedForm.id;
      if (!id) {
        return;
      }

      const response = await DataService.deleteForm(id);
      if (response.status == 200) {
        this.showDeleteDialog = false;
        this.$root.$emit('raise_alert', { color: 'success', text: 'Form deleted successfully' });
        
        await this.getClientForms();
        this.selectedForm = null;
        this.selectedFormIndex = null;
        
        if (this.$route.query.id) {
          this.$router.replace({ query: { tab: 'form management' } });
        }
      } else {
        this.$root.$emit('raise_alert', { color: 'error', text: response.data || 'Failed to delete form' });
      }
    },
    async deleteCategory() {
      const response = await DataService.deleteClientDiseaseCategory(this.selectedCategory.id);
      if (response.status == 200) {
        this.showDeleteCategoryDialog = false;
        this.$root.$emit('raise_alert', { color: 'success', text: 'Disease grouping deleted successfully' });
        
        await this.getClientDiseaseCategories();
        this.selectedCategory = null;
        this.selectedCategoryIndex = null;
      } else {
        this.$root.$emit('raise_alert', { color: 'error', text: response.data || 'Failed to delete disease grouping' });
      }
    },
    selectForm(form, index) {
      this.selectedCategory = null;
      this.selectedCategoryIndex = null;
      this.selectedForm = form;
      this.selectedFormIndex = index;

      if (form.id && Number(this.$route.query.id) !== form.id) {
        this.$router.replace({ path: this.$route.path, query: { tab: 'form management', id: form.id }});
      }
    },
    selectCategory(category, index) {
      this.selectedForm = null;
      this.selectedFormIndex = null;

      if (category) {
        this.selectedCategory = category;
        this.selectedCategoryIndex = index;
      } else {
        this.selectedCategory = {
          category: '',
          openDentalDiseaseIds: [],
        };
        this.selectedCategoryIndex = null;
      }
    },
    async goToTemplateEditor() {
      const saveResult = await this.saveForm(true);
      if (saveResult) {
        this.$router.push({
          name: 'EmailTemplateEditor',
          query: {
            id: this.$route.query.id,
            type: 'form',
          },
        });
      }
    },
  },
  async mounted() {
    this.loading = true;

    await Promise.all([
      this.getDiseaseDefinitions(),
      this.getClientDiseaseCategories(),
      this.getClientForms(),
    ]);

    if (this.$route.query.id) {
      const formIndex = this.forms.findIndex(f => f.id === Number(this.$route.query.id));
      if (formIndex !== -1) {
        this.selectForm(this.forms[formIndex], formIndex);
      }
    }

    this.loading = false;
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';

$form-card-border-color: #e2e8f0;
$section-title-color: #1f2937;
$modal-description-color: #6b7280;
$preview-overlay-color: rgba(0, 0, 0, 0.2);
$preview-empty-bg-color: #f8fafc;
$preview-empty-text-color: #9ca3af;

.loading-container {
  text-align: center;
  padding: 80px 24px;
}

.loading-spinner {
  display: block;
  margin: 40px auto;
}

.form-sidebar {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  height: fit-content;
  
  .sidebar-header {
    padding: 24px 24px 16px 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .sidebar-title {
      font-size: 18px;
      font-weight: 600;
      color: #2d3748;
      margin: 0;
      font-family: 'Inter', sans-serif;
    }

    .btn-sm {
      padding: 6px 12px !important;
      font-size: 13px !important;
      min-width: auto !important;
    }
  }
  
  .sidebar-content {
    padding: 16px;
  }
}

.form-list {
  .no-results {
    text-align: center;
    padding: 40px 16px;
    
    .no-results-icon {
      font-size: 48px;
      color: #cbd5e0;
      margin-bottom: 12px;
    }
    
    .no-results-text {
      color: #718096;
      margin: 0;
      font-size: 14px;
    }
  }
  
  .forms-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .form-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background-color: #edf2f7;
      border-color: #cbd5e0;
    }
    
    &.selected {
      background-color: #f0f9e8 !important;
      border-color: #B0D35E !important;
      
      .form-name {
        color: #414a5b !important;
        font-weight: 600 !important;
      }
    }
    
    .form-icon {
      color: #133b54 !important;
      margin-right: 12px !important;
      font-size: 20px !important;
    }
    
    .form-name {
      font-weight: 500 !important;
      color: #1f2937 !important;
      font-size: 14px !important;
    }
  }
}

.no-selection {
  text-align: center;
  padding: 80px 24px;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid #e2e8f0;
  
  .no-selection-icon {
    font-size: 64px;
    color: #cbd5e0;
    margin-bottom: 16px;
  }
  
  .no-selection-text {
    color: #718096;
    margin: 0;
    font-size: 16px;
  }
}

.info-section {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid #e2e8f0;
  padding: 24px;
}

.section-header-with-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: $section-title-color;
    margin: 0;
    border: none;
  }
}

.subsection-title {
  font-size: 14px;
  font-weight: 600;
  color: $section-title-color;
  margin: 0 0 12px 0;
}

.form-preview {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  margin-bottom: 24px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e0;
    box-shadow: $shadow-sm;
  }

  .preview-container {
    position: relative;
    height: 400px;

    .preview-iframe {
      width: 100%;
      height: 100%;
      border: none;
    }

    .preview-overlay {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background-color: $preview-overlay-color;
    }
  }

  .preview-empty {
    text-align: center;
    padding: 60px 24px;
    background-color: $preview-empty-bg-color;

    .preview-empty-icon {
      font-size: 48px;
      color: #cbd5e0;
      margin-bottom: 12px;
    }

    .preview-empty-text {
      color: $preview-empty-text-color;
      margin: 0;
      font-size: 14px;
    }
  }
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;
}

.modal-description {
  color: $modal-description-color;
  font-size: 14px;
  margin-top: 12px;
}

.modal-question {
  font-size: 14px;
  margin: 0;
}
</style>
